import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import FaceIcon from '@mui/icons-material/Face';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import {
	DataGrid,
	FilterAsyncAutoComplete,
	FilterDateRange,
	FilterSelect,
	AccessLogChip,
	ActionCell,
	UserChip,
} from '@repo/components';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getGroupLogs } from 'src/api/supervisor/logs';
import { getMyGroups } from 'src/api/supervisor/groups';
import { startOfDay, subMonths, endOfDay } from 'date-fns';

interface SearchParams {
	page?: number;
	sort?: string;
	type?: 'AccessLog' | 'UserLog';
	userId?: string;
	from?: string;
	until?: string;
}

export const Route = createFileRoute('/supervisor_/logs')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
		type: search.type as 'AccessLog' | 'UserLog' | undefined,
		userId: search.userId as string | undefined,
		from: search.from as string | undefined,
		until: search.until as string | undefined,
	}),
	loader: async () => {
		const groups = await getMyGroups();
		return { groups };
	},
	component: LogsPage,
});

const methods: Record<string, string> = {
	nfc: 'NFC',
	qr: 'QR',
	ibeacon: 'BLE / Smartphone',
};

const baseColumns = [
	{
		field: 'timestamp',
		headerName: 'Date',
		width: 170,
		valueFormatter: (params: { value: string }) =>
			`${new Date(params.value).toLocaleDateString('en-GB')} ${new Date(params.value).toLocaleTimeString('en-GB')}`,
	},
];

const userColumns = [
	{
		field: 'actor',
		headerName: 'Actor',
		flex: 1,
		renderCell: (c: { value: { id: string; firstName: string; lastName: string } }) =>
			c.value && <UserChip user={c.value} />,
		sortable: false,
	},
	{
		field: 'action',
		headerName: 'Action',
		flex: 3,
		renderCell: (c: { value: unknown; row: { type: string } }) =>
			c.value && <ActionCell action={c.value} log={c.row} logType={c.row.type} />,
		sortable: false,
	},
];

function LogsPage() {
	const { groups } = Route.useLoaderData();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();

	const selectedGroupId = groups[0]?.id;
	const typeFilter = (search.type || 'AccessLog') as 'AccessLog' | 'UserLog';

	const defaultDateRange = useMemo(() => {
		const now = new Date();
		return {
			from: startOfDay(subMonths(now, 1)).toISOString(),
			until: endOfDay(now).toISOString(),
		};
	}, []);

	// Redirect to AccessLog if type is not set or invalid
	useEffect(() => {
		if (!search.type || !['AccessLog', 'UserLog'].includes(search.type)) {
			updateQueryParams({ type: 'AccessLog' });
		}
	}, [search.type]);

	const {
		data: logsData,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'supervisor-group-logs',
			selectedGroupId,
			search.page,
			search.sort,
			search.type,
			search.userId,
			search.from || defaultDateRange.from,
			search.until || defaultDateRange.until,
		],
		queryFn: () =>
			getGroupLogs(selectedGroupId, {
				page: search.page,
				sort: search.sort || 'timestamp:desc',
				type: typeFilter,
				userId: search.userId,
				from: search.from || defaultDateRange.from,
				until: search.until || defaultDateRange.until,
			}),
		enabled: !!selectedGroupId && !!search.type && ['AccessLog', 'UserLog'].includes(search.type),
	});

	const revalidateLogs = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['supervisor-group-logs', selectedGroupId] });
		refetch();
	}, [queryClient, selectedGroupId, refetch]);

	const handleSort = ([sort]: [{ field: string; sort: 'asc' | 'desc' } | undefined]) => {
		if (sort === undefined) return;
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleTypeFilterChange = (type: 'AccessLog' | 'UserLog') => {
		updateQueryParams({ type });
	};

	const handleUserFilterChange = (userId: string) => {
		updateQueryParams({ userId });
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const accessColumns = useMemo(
		() => [
			{
				field: 'user',
				headerName: 'User',
				flex: 1,
				renderCell: (c: { value: { id: string; firstName: string; lastName: string } }) =>
					c.value && <UserChip user={c.value} href={`/supervisor/users?userId=${c.value.id}`} />,
				sortable: false,
			},
			{
				field: 'zone.name',
				headerName: 'Zone',
				flex: 1,
			},
			{
				field: 'data.origin',
				headerName: 'Token',
				flex: 1,
				sortable: false,
				valueFormatter: (c: { value: string }) => methods[c.value] || '',
			},
			{
				field: 'data.faceResults.matchFound',
				headerName: 'Biometry',
				flex: 1,
				sortable: false,
				renderCell: (c: { value: boolean; row: { data?: { faceResults?: { mRSID?: { success: boolean } } } } }) => {
					if (!c.value) return '';
					const highConfidence = c.row?.data?.faceResults?.mRSID?.success;
					const label = highConfidence ? 'High' : 'Medium';
					const color = highConfidence ? 'success' : 'warning';
					return (
						<Chip
							size="small"
							variant="filled"
							icon={<FaceIcon />}
							label={label}
							color={color as 'success' | 'warning'}
						/>
					);
				},
			},
			{
				field: 'status',
				headerName: 'Status',
				width: 200,
				sortable: false,
				renderCell: (c: { row: unknown }) => <AccessLogChip log={c.row} />,
			},
		],
		[],
	);

	const columns = useMemo(() => {
		return [...baseColumns, ...(typeFilter === 'AccessLog' ? accessColumns : userColumns)];
	}, [typeFilter, accessColumns]);

	if (!groups || groups.length === 0) {
		return (
			<div className="p-4 text-center flex flex-col items-center justify-center h-full">
				<div>
					<img src="/public/blank_canvas.svg" className="h-150 m-auto mb-4 mt-2" alt="No groups" />
				</div>
				<div className="font-bold opacity-50">
					Your supervisor access is not yet fully configured.
					<br />
					Contact your administrator.
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="flex flex-row flex-wrap gap-2 mt-4">
				<FilterSelect
					options={[
						{ label: 'User Logs', value: 'UserLog' },
						{ label: 'Access Logs', value: 'AccessLog' },
					]}
					value={typeFilter}
					onChange={(type) => handleTypeFilterChange(type as 'AccessLog' | 'UserLog')}
					sx={{ width: 330 }}
				/>
				<FilterAsyncAutoComplete
					key="userFilter"
					label="Filter User"
					value={search.userId || ''}
					onChange={(userId) => handleUserFilterChange(userId as string)}
					dataSource="/api/data-sources/users/data"
					name="userIds"
					multiple={false}
					sx={{ width: 330 }}
				/>
				<FilterDateRange
					label="Filter Date Range"
					value={
						search.from || search.until
							? {
									from: search.from,
									until: search.until,
								}
							: defaultDateRange
					}
					onChange={handleDateFilterChange}
					sx={{ width: 330 }}
				/>
			</div>

			<Paper variant="outlined" sx={{ marginTop: 2 }}>
				<DataGrid
					data={logsData?.data || []}
					columns={columns}
					isLoading={isLoading}
					rowCount={logsData?.count || 0}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={handlePaginationModelChange}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(search.sort || 'timestamp:desc')]}
					pageSize={logsData?.pageSize || 50}
					pageSizeOptions={[logsData?.pageSize || 50]}
					page={logsData?.page || 0}
					pagination
					getRowClassName={(p) => `!cursor-default ${p.row.markedForReview ? 'error' : ''}`}
					onReload={revalidateLogs}
				/>
			</Paper>
		</>
	);
}
